import { useCallback, useEffect, useState } from "react";
import {
  fetchRemoteAppVersion,
  isClientBuildOutdated,
  type RemoteAppVersion,
} from "../utils/pwaVersionCheck";
import { applyPwaHardUpdate } from "../utils/pwaApplyUpdate";
import { isStandaloneMode } from "../utils/pwaInstallPrompt";

const CHECK_INTERVAL_MS = 15 * 60 * 1000;

export function PwaUpdateBanner() {
  const [open, setOpen] = useState(false);
  const [remote, setRemote] = useState<RemoteAppVersion | null>(null);
  const [busy, setBusy] = useState(false);

  const runCheck = useCallback(async () => {
    if (typeof window === "undefined") return;
    const swControlled = !!navigator.serviceWorker?.controller;
    const standalone = isStandaloneMode();
    if (!swControlled && !standalone) return;

    try {
      const reg = await navigator.serviceWorker.getRegistration();
      await reg?.update();
    } catch {
      /* ignore */
    }

    const data = await fetchRemoteAppVersion();
    if (!data) return;
    if (isClientBuildOutdated(data)) {
      setRemote(data);
      setOpen(true);
    } else {
      setOpen(false);
      setRemote(null);
    }
  }, []);

  useEffect(() => {
    runCheck();
    const t = window.setInterval(runCheck, CHECK_INTERVAL_MS);
    const onVis = () => {
      if (document.visibilityState === "visible") runCheck();
    };
    document.addEventListener("visibilitychange", onVis);

    return () => {
      window.clearInterval(t);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [runCheck]);

  const onUpdate = async () => {
    setBusy(true);
    try {
      await applyPwaHardUpdate();
    } catch {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div className="pwa-update-banner" role="status">
      <div className="pwa-update-banner__text">
        <strong>Доступна новая версия.</strong>
        <span>
          {" "}
          Обновите приложение, чтобы курс и уведомления работали без сбоев.
          {remote?.version ? ` Версия на сервере: ${remote.version}.` : ""}
          {remote?.buildId ? ` Сборка: ${remote.buildId.slice(0, 8)}.` : ""}
          {remote?.builtAt ? ` Дата сборки: ${new Date(remote.builtAt).toLocaleString()}.` : ""}
        </span>
      </div>
      <button
        type="button"
        className="pwa-update-banner__btn"
        disabled={busy}
        onClick={onUpdate}
      >
        {busy ? "Обновление…" : "ОБНОВИТЬ ПРИЛОЖЕНИЕ"}
      </button>
    </div>
  );
}
